import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import MonthPicker from '../MonthPicker/index.js';
import {
  FaSearch, FaPlus, FaEye, FaEdit, FaTrash, FaCheck, FaTimes,
  FaFileExcel, FaFileDownload, FaCheckDouble, FaGift, FaHandHoldingUsd,
  FaClock, FaUndoAlt, FaFileInvoiceDollar, FaExclamationTriangle,
  FaUserCircle, FaCloudUploadAlt, FaSave, FaPaperPlane, FaMinus,
  FaTrophy, FaChartLine, FaCheckCircle, FaExclamationCircle,
  FaPlusCircle, FaMinusCircle, FaWallet
} from 'react-icons/fa';
import {
  commonSaveData,
  commonGetData,
  commonGetDataOne,
  commonGetDataTwo,
  initCommonState,
} from '../../../../redux/slices/commonSlice';
import {
  calculateEarnedSalaryPath,
  addAdvancePath,
  addIncentiveStaffPath,
  addIncentiveTypeStaffPath,
  addBonusWagePath,
  addOtherEarningsPath,
  addOtherDeductionsPath,
  addReimbursementStaffPath,
  getAdvanceListPath,
  getAdvanceSummaryPath,
  getAdvanceByStaffPath,
  approveAdvancePath,
  bulkApproveAdvancePath,
  markAdvanceForPayrollPath,
  updateAdvancePath,
  deleteAdvancePath,
} from '../../../../services/config';
import { saveUpdateIncentive, initIncentiveState } from '../../../../redux/slices/incentiveSlice';
import './IncentivesAdvances.css';
import {
  getAdvanceStatusOptions, getIncentiveCategories, getAdvanceCategories,
  getBonusTypes, getDeductionTypes, getAdvanceBranches, getAdvanceDepartments,
} from '../../payrollDB';
import { validatePayrollForm, isFormValid } from '../../payrollValidation';

const AI = {
  statusOptions:        getAdvanceStatusOptions(),
  incentiveCategories:  getIncentiveCategories(),
  advanceCategories:    getAdvanceCategories(),
  bonusTypes:           getBonusTypes(),
  deductionTypes:       getDeductionTypes(),
  branches:             getAdvanceBranches(),
  departments:          getAdvanceDepartments(),
};

const IncentivesAdvances = () => {
  const user = JSON.parse(localStorage.getItem('user_data')) || {};
  const dispatch = useDispatch();
  const commonStore = useSelector((state) => state.common);
  const incentiveStore = useSelector((state) => state.incentive);

  // ── AG Grid ref ──
  const gridRef = useRef(null);

  // ==================== State Management ====================
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Derive YYYY-MM from current date for the month picker
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedRows, setSelectedRows] = useState([]);

  // API-driven state — replaces all mock data
  const [transactions, setTransactions] = useState([]);
  const [kpis, setKpis] = useState({
    totalIncentives: 0, totalAdvances: 0, pendingApprovals: 0,
    totalRecoveries: 0, activeAdvances: 0, exceptions: 0,
  });
  const [employeesList, setEmployeesList] = useState([]);
  const [staffAdvanceSnapshot, setStaffAdvanceSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEntryType, setSelectedEntryType] = useState('incentive');
  const [selectedEmployeeForEntry, setSelectedEmployeeForEntry] = useState(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  // Form data for different entry types
  const [incentiveData, setIncentiveData] = useState({
    category: 'Performance', amount: '', effectiveMonth: defaultMonth,
    recurring: 'One Time', taxable: true, description: '',
  });
  const [advanceData, setAdvanceData] = useState({
    category: 'Salary Advance', amount: '',
    disbursementDate: new Date().toISOString().split('T')[0],
    recoveryStartMonth: '', recoveryMethod: 'emi',
    installments: '5', allowEarlyClosure: true, approvalRequired: true, notes: '',
  });
  const [bonusData, setBonusData] = useState({ type: 'Festival Bonus', amount: '', effectiveMonth: defaultMonth, notes: '' });
  const [deductionData, setDeductionData] = useState({ type: 'Asset Damage', amount: '', recoveryMode: 'Current Payroll (Full)', remarks: '' });
  const [recoveryData, setRecoveryData] = useState({ advanceCase: '', recoveryAmount: '' });
  const [pettyCashData, setPettyCashData] = useState({ amount: '', date: new Date().toISOString().split('T')[0], purpose: '', notes: '' });
  const [internalComments, setInternalComments] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // ── Helpers ──────────────────────────────────────────────
  const getSelectedDate = () => `${selectedMonth}-01`;

  // ── 1. Load table data from API (getAdvanceListPath) ─────
  const loadTransactions = useCallback(() => {
    dispatch(commonGetData(
      `${getAdvanceListPath}?userId=${user.user_id}&selectedDate=${getSelectedDate()}`
    ));
  }, [selectedMonth, user.user_id]);

  // ── 2. Load KPI summary (getAdvanceSummaryPath) ──────────
  const loadSummary = useCallback(() => {
    dispatch(commonGetDataOne(
      `${getAdvanceSummaryPath}?userId=${user.user_id}&selectedDate=${getSelectedDate()}`
    ));
  }, [selectedMonth, user.user_id]);

  // ── 3. Load employee list via calculateEarnedSalary ──────
  const loadEmployeeList = useCallback(() => {
    dispatch(commonSaveData({
      path: calculateEarnedSalaryPath,
      data: {
        userId: user.user_id,
        selectedDate: getSelectedDate(),
        branchId: selectedBranch || null,
        shiftId: null,
      },
    }));
  }, [selectedMonth, selectedBranch, user.user_id]);

  // ── On mount + filter change: load all data ───────────────
  useEffect(() => {
    loadTransactions();
    loadSummary();
    loadEmployeeList();
  }, [selectedMonth, selectedBranch]);

  // ── 4. Load staff advance snapshot when employee selected ─
  useEffect(() => {
    if (selectedEmployeeForEntry?.staffId) {
      dispatch(commonGetDataTwo(
        `${getAdvanceByStaffPath}?userId=${user.user_id}&staffId=${selectedEmployeeForEntry.staffId}`
      ));
    }
  }, [selectedEmployeeForEntry]);

  // ── Redux response handlers ───────────────────────────────
  useEffect(() => {
    // commonGetData → advance list
    if (commonStore.commonGetSuccess && Array.isArray(commonStore.commonGetData)) {
      setTransactions(commonStore.commonGetData);
    }
    if (commonStore.commonGetError) {
      setTransactions([]);
    }

    // commonGetDataOne → KPI summary
    if (commonStore.commonGetOneSuccess && commonStore.commonGetOneData) {
      setKpis(commonStore.commonGetOneData);
    }

    // commonGetDataTwo → staff advance snapshot
    if (commonStore.commonGetTwoSuccess && commonStore.commonGetTwoData) {
      setStaffAdvanceSnapshot(commonStore.commonGetTwoData);
    }

    // commonSaveData → employee list (calculateEarnedSalary) OR submit entry
    if (commonStore.commonSuccess) {
      if (commonStore.commonData?.staffDetails) {
        // Employee list loaded
        setEmployeesList(
          (commonStore.commonData.staffDetails || []).map(s => ({
            staffId: s.staffId,
            id: s.employeeId,
            name: s.staffName,
            department: s.department || '',
            designation: s.jobTitle || '',
            branch: s.branch || '',
            grossSalary: s.CostToCompany || 0,
            avatar: s.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(s.staffName)}&background=8660F8&color=fff`,
          }))
        );
      } else {
        // A POST action succeeded (add/approve/delete/etc.) — refresh table
        toast.success(commonStore.commonMessage || 'Done');
        loadTransactions();
        loadSummary();
        setShowAddModal(false);
      }
      dispatch(initCommonState());
    }
    if (commonStore.commonError) {
      toast.error(commonStore.commonMessage || 'Something went wrong');
      dispatch(initCommonState());
    }

    // incentiveSlice → add incentive type or add incentive
    if (incentiveStore?.incSuccess) {
      toast.success(incentiveStore.incMessage || 'Incentive added');
      loadTransactions();
      loadSummary();
      setShowAddModal(false);
      dispatch(initIncentiveState());
    }
    if (incentiveStore?.incError) {
      toast.error(incentiveStore.incMessage || 'Failed to add incentive');
      dispatch(initIncentiveState());
    }
  }, [commonStore, incentiveStore]);

  // Filtered employees for search
  const filteredEmployees = useMemo(() =>
    employeesList.filter(emp =>
      emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      emp.id.toLowerCase().includes(employeeSearchTerm.toLowerCase())
    ), [employeesList, employeeSearchTerm]);

  // Filtered transactions based on tabs and search
  const filteredTransactions = useMemo(() => {
    let filtered = Array.isArray(transactions) ? transactions : [];
    if (activeTab !== 'all') filtered = filtered.filter(t => t.entryType === activeTab);
    if (searchQuery) filtered = filtered.filter(t =>
      (t.employee?.name || t.staffName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.employee?.id || t.employeeId || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (selectedStatus !== 'all') filtered = filtered.filter(t => t.status === selectedStatus);
    return filtered;
  }, [transactions, activeTab, searchQuery, selectedStatus]);

  // EMI Preview
  const emiPreview = useMemo(() => {
    const amount = parseFloat(advanceData.amount) || 0;
    const installments = parseInt(advanceData.installments) || 1;
    return installments > 0 ? amount / installments : 0;
  }, [advanceData.amount, advanceData.installments]);

  // Selected employee details
  const selectedEmployeeDetails = selectedEmployeeForEntry || employeesList[0];

  // Net impact calculation
  const netImpact = useMemo(() => {
    if (!selectedEmployeeForEntry) return { currentNet: 0, newAmount: 0, projectedNet: 0 };

    const currentGross = selectedEmployeeForEntry.grossSalary || 45000;
    const existingDeductions = 2500;
    const currentNet = currentGross - existingDeductions;

    let newAmount = 0;
    if (selectedEntryType === 'incentive' || selectedEntryType === 'bonus') {
      newAmount = parseFloat(selectedEntryType === 'incentive' ? incentiveData.amount : bonusData.amount) || 0;
    } else if (selectedEntryType === 'advance' && advanceData.recoveryMethod === 'emi') {
      newAmount = -emiPreview;
    } else if (selectedEntryType === 'deduction') {
      newAmount = -(parseFloat(deductionData.amount) || 0);
    } else if (selectedEntryType === 'recovery') {
      newAmount = -(parseFloat(recoveryData.recoveryAmount) || 0);
    }

    return {
      currentNet,
      newAmount,
      projectedNet: currentNet + newAmount,
    };
  }, [selectedEmployeeForEntry, selectedEntryType, incentiveData.amount, bonusData.amount, advanceData.recoveryMethod, emiPreview, deductionData.amount, recoveryData.recoveryAmount]);

  // Handlers
  const handleSelectAll = (e) => {
    if (gridRef.current?.api) {
      if (e.target.checked) {
        gridRef.current.api.selectAll();
      } else {
        gridRef.current.api.deselectAll();
      }
    }
  };

  const handleAddEntry = () => {
    setShowAddModal(true);
    setSelectedEntryType('incentive');
    setSelectedEmployeeForEntry(null);
    setEmployeeSearchTerm('');
    setIncentiveData({
      category: 'Performance',
      amount: '',
      effectiveMonth: 'April 2026',
      recurring: 'One Time',
      taxable: true,
      description: '',
    });
    setAdvanceData({
      category: 'Salary Advance',
      amount: '',
      disbursementDate: new Date().toISOString().split('T')[0],
      recoveryStartMonth: 'May 2026',
      recoveryMethod: 'emi',
      installments: '5',
      allowEarlyClosure: true,
      approvalRequired: true,
      notes: '',
    });
    setInternalComments('');
    setUploadedFile(null);
  };

  const handleSubmitEntry = () => {
    if (!selectedEmployeeForEntry) {
      toast.error('Please select an employee');
      return;
    }
    const staffId = selectedEmployeeForEntry.staffId;
    const selectedDate = getSelectedDate();

    switch (selectedEntryType) {
      case 'incentive':
        if (!incentiveData.amount) { toast.error('Please enter incentive amount'); return; }
        dispatch(saveUpdateIncentive({
          path: addIncentiveStaffPath,
          data: {
            userId: user.user_id,
            staffId,
            type: incentiveData.category,
            amount: incentiveData.amount,
            incentiveDate: incentiveData.effectiveMonth ? `${incentiveData.effectiveMonth}-01` : selectedDate,
            notes: incentiveData.description,
          },
        }));
        break;

      case 'advance':
        if (!advanceData.amount) { toast.error('Please enter advance amount'); return; }
        dispatch(commonSaveData({
          path: addAdvancePath,
          data: {
            userId: user.user_id,
            staffId,
            amount: advanceData.amount,
            advanceType: advanceData.category,
            recoveryStartDate: advanceData.recoveryStartMonth ? `${advanceData.recoveryStartMonth}-01` : '',
            installments: advanceData.installments,
            notes: advanceData.notes,
          },
        }));
        break;

      case 'bonus':
        if (!bonusData.amount) { toast.error('Please enter bonus amount'); return; }
        dispatch(commonSaveData({
          path: addBonusWagePath,
          data: {
            userId: user.user_id,
            staffId,
            type: bonusData.type,
            amount: bonusData.amount,
            paymentDate: selectedDate,
            notes: bonusData.notes,
          },
        }));
        break;

      case 'deduction':
        if (!deductionData.amount) { toast.error('Please enter deduction amount'); return; }
        dispatch(commonSaveData({
          path: addOtherDeductionsPath,
          data: {
            userId: user.user_id,
            staffId,
            type: deductionData.type,
            amount: deductionData.amount,
            paymentDate: selectedDate,
            notes: deductionData.remarks,
          },
        }));
        break;

      case 'recovery':
        if (!recoveryData.recoveryAmount) { toast.error('Please enter recovery amount'); return; }
        dispatch(commonSaveData({
          path: addOtherDeductionsPath,
          data: {
            userId: user.user_id,
            staffId,
            type: 'Advance Recovery',
            amount: recoveryData.recoveryAmount,
            paymentDate: selectedDate,
            notes: internalComments,
          },
        }));
        break;

      default:
        break;
    }
  };

  // ── NEW: Approve single advance ───────────────────────────
  const handleApproveRow = useCallback((advanceId) => {
    dispatch(commonSaveData({
      path: approveAdvancePath,
      data: { userId: user.user_id, advanceId, status: 'approved' },
    }));
  }, [user.user_id]);

  // ── NEW: Delete advance entry ─────────────────────────────
  const handleDeleteRow = useCallback((advanceId) => {
    dispatch(commonSaveData({
      path: deleteAdvancePath,
      data: { userId: user.user_id, advanceId },
    }));
  }, [user.user_id]);

  // ── NEW: Bulk approve selected rows ──────────────────────
  const handleBulkApprove = useCallback(() => {
    if (!selectedRows.length) { toast.error('Select at least one entry'); return; }
    dispatch(commonSaveData({
      path: bulkApproveAdvancePath,
      data: { userId: user.user_id, advanceIds: selectedRows },
    }));
  }, [selectedRows, user.user_id]);

  // ── NEW: Mark selected rows for payroll ──────────────────
  const handleMarkForPayroll = useCallback(() => {
    if (!selectedRows.length) { toast.error('Select at least one entry'); return; }
    dispatch(commonSaveData({
      path: markAdvanceForPayrollPath,
      data: { userId: user.user_id, advanceIds: selectedRows, payrollMonth: getSelectedDate() },
    }));
  }, [selectedRows, selectedMonth, user.user_id]);

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'pending': return 'status-pending';
      case 'processed': return 'status-processed';
      case 'recovered': return 'status-recovered';
      case 'rejected': return 'status-rejected';
      default: return 'status-pending';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'incentive': return 'type-incentive';
      case 'advance': return 'type-advance';
      case 'recovery': return 'type-recovery';
      case 'bonus': return 'type-bonus';
      case 'deduction': return 'type-deduction';
      case 'petty-cash': return 'type-petty-cash';
      default: return 'type-incentive';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'incentive': return <FaGift className="text-xs" />;
      case 'advance': return <FaHandHoldingUsd className="text-xs" />;
      case 'bonus': return <FaTrophy className="text-xs" />;
      case 'deduction': return <FaMinus className="text-xs" />;
      case 'recovery': return <FaUndoAlt className="text-xs" />;
      case 'petty-cash': return <FaWallet className="text-xs" />;
      default: return <FaGift className="text-xs" />;
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // ── AG Grid column definitions ──
  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      pinned: 'left',
      resizable: false,
      sortable: false,
      filter: false,
      headerClass: 'ag-checkbox-header',
      cellClass: 'ag-checkbox-cell',
    },
    {
      headerName: 'Employee',
      field: 'employee',
      minWidth: 160,
      cellRenderer: (params) => {
        const emp = params.value;
        return (
          <div className="ag-employee-cell">
            <img src={emp.avatar} alt={emp.name} className="ag-emp-avatar" />
            <div>
              <div className="ag-emp-name">{emp.name}</div>
              <div className="ag-emp-id">{emp.id}</div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: 'Department',
      field: 'employee',
      colId: 'department',
      minWidth: 110,
      valueGetter: (p) => p.data.employee.department,
      cellRenderer: (params) => (
        <span className="ag-dept-text">{params.value}</span>
      ),
    },
    {
      headerName: 'Entry Type',
      field: 'entryType',
      minWidth: 110,
      cellRenderer: (params) => {
        const type = params.value;
        const icons = {
          incentive: <FaGift />,
          advance: <FaHandHoldingUsd />,
          bonus: <FaTrophy />,
          deduction: <FaMinus />,
          recovery: <FaUndoAlt />,
        };
        const classes = {
          incentive: 'type-incentive',
          advance: 'type-advance',
          recovery: 'type-recovery',
          bonus: 'type-bonus',
          deduction: 'type-deduction',
        };
        return (
          <span className={`type-badge ${classes[type] || ''}`}>
            {icons[type]} {type.charAt(0).toUpperCase() + type.slice(1)}
          </span>
        );
      },
    },
    {
      headerName: 'Reason',
      field: 'reason',
      minWidth: 140,
      cellRenderer: (params) => <span className="ag-reason-text">{params.value}</span>,
    },
    {
      headerName: 'Amount',
      field: 'amount',
      minWidth: 100,
      cellRenderer: (params) => {
        const neg = params.data.entryType === 'deduction' || params.data.entryType === 'recovery';
        return (
          <span className={neg ? 'amount-negative' : 'amount-positive'}>
            ₹{params.value.toLocaleString()}
          </span>
        );
      },
    },
    {
      headerName: 'Recovery Plan',
      field: 'recoveryPlan',
      minWidth: 120,
      cellRenderer: (params) => <span className="recovery-plan">{params.value}</span>,
    },
    {
      headerName: 'Date',
      field: 'date',
      minWidth: 100,
      cellRenderer: (params) => <span className="date-text">{params.value}</span>,
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      minWidth: 100,
      cellRenderer: (params) => <span className="created-by">{params.value}</span>,
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 100,
      cellRenderer: (params) => {
        const map = {
          approved: 'status-approved',
          pending: 'status-pending',
          processed: 'status-processed',
          recovered: 'status-recovered',
          rejected: 'status-rejected',
        };
        return (
          <span className={`status-chip ${map[params.value] || 'status-pending'}`}>
            {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          </span>
        );
      },
    },
    {
      headerName: 'Payroll',
      field: 'payrollMonth',
      minWidth: 90,
      cellRenderer: (params) => <span className="payroll-month">{params.value}</span>,
    },
    {
      headerName: 'Actions',
      field: 'id',
      colId: 'actions',
      minWidth: 130,
      maxWidth: 140,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px' },
      cellRenderer: (params) => (
        <div className="action-buttons-cell">
          <button className="action-btn" title="View"    onClick={(e) => e.stopPropagation()}><FaEye /></button>
          <button className="action-btn" title="Edit"    onClick={(e) => e.stopPropagation()}><FaEdit /></button>
          <button className="action-btn success" title="Approve" onClick={(e) => { e.stopPropagation(); handleApproveRow(params.data.id); }}><FaCheck /></button>
          <button className="action-btn error"   title="Delete"  onClick={(e) => { e.stopPropagation(); handleDeleteRow(params.data.id); }}><FaTrash /></button>
        </div>
      ),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    suppressMovable: false,
    cellStyle: { display: 'flex', alignItems: 'center' },
  }), []);

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const selected = gridRef.current.api.getSelectedRows();
      setSelectedRows(selected.map(r => r.id));
    }
  }, []);

  return (
    <div className="incentives-advances-container">
      <ToastContainer position="top-right" autoClose={2000} />

      {/* Main Content */}
      <main className="main-content">
        {/* Page Header */}
        <header className="page-header">
          <div>
            <h1 className="page-title">Incentives & Advances Center</h1>
            <p className="page-subtitle">Manage additions, recoveries, and employee adjustments before payroll run • April 2026</p>
          </div>
        </header>

        {/* Filter & Action Bar */}
        <section className="filter-bar">
          <div className="filter-controls">

            {/* Month */}
           {/* Month */}
<div className="field-wrap field-month" style={{ position: "relative" }}>
  <span style={{
    position: "absolute",
    left: 9,
    top: "50%",
    transform: "translateY(-50%)",
    pointerEvents: "none",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    color: "var(--pay-text-muted, #888)",
  }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  </span>
  <input
    type="month"
    className="filter-select"
    value={selectedMonth}
    onChange={(e) => setSelectedMonth(e.target.value)}
    onClick={(e) => {
      try { e.target.showPicker(); } catch (_) {}
    }}
    onFocus={(e) => {
      try { e.target.showPicker(); } catch (_) {}
    }}
    style={{
      paddingLeft: "28px",
      paddingRight: "10px",
      cursor: "pointer",
      colorScheme: "dark",          /* makes the native picker match dark UI */
    }}
  />
</div>

            {/* Branch */}
            <div className="field-wrap field-branch">
              <select className="filter-select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                <option value="">All Branches</option>
                {AI.branches.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <span className="fi-right">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>

            {/* Department */}
            <div className="field-wrap field-dept">
              <select className="filter-select" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {AI.departments.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <span className="fi-right">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>

            {/* Status */}
            <div className="field-wrap field-status">
              <select className="filter-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">All Status</option>
                {AI.statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <span className="fi-right">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </span>
            </div>

            {/* Search */}
            <div className="field-wrap field-search search-input-wrapper">
              <span className="fi-left">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input
                type="text"
                placeholder="Search employee..."
                className="filter-select"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: "26px", paddingRight: "10px", cursor: "text" }}
              />
            </div>
          </div>
          <div className="action-buttons">
            <button className="btn-ghost"><FaFileExcel className="mr-2" /> Import Excel</button>
            <button className="btn-ghost"><FaFileDownload className="mr-2" /> Export Report</button>
            <button className="btn-secondary" onClick={handleBulkApprove}><FaCheckDouble className="mr-2" /> Bulk Approve</button>
            <button className="btn-primary" onClick={handleAddEntry}><FaPlus className="mr-2" /> Add New Entry</button>
          </div>
        </section>

        {/* KPI Cards */}
        <section className="kpi-grid">
          <div className="kpi-card kpi-success">
            <div className="kpi-icon">
              <FaGift />
            </div>
            <div className="kpi-value">₹{(kpis.totalIncentives / 1000).toFixed(1)}L</div>
            <div className="kpi-label">Total Incentives</div>
          </div>
          <div className="kpi-card kpi-warning">
            <div className="kpi-icon">
              <FaHandHoldingUsd />
            </div>
            <div className="kpi-value">₹{(kpis.totalAdvances / 1000).toFixed(1)}L</div>
            <div className="kpi-label">Advances Issued</div>
          </div>
          <div className="kpi-card kpi-accent">
            <div className="kpi-icon">
              <FaClock />
            </div>
            <div className="kpi-value">{kpis.pendingApprovals}</div>
            <div className="kpi-label">Pending Approvals</div>
          </div>
          <div className="kpi-card kpi-blue">
            <div className="kpi-icon">
              <FaUndoAlt />
            </div>
            <div className="kpi-value">₹{(kpis.totalRecoveries / 1000).toFixed(1)}L</div>
            <div className="kpi-label">Recoveries</div>
          </div>
          <div className="kpi-card kpi-purple">
            <div className="kpi-icon">
              <FaFileInvoiceDollar />
            </div>
            <div className="kpi-value">{kpis.activeAdvances}</div>
            <div className="kpi-label">Active Advances</div>
          </div>
          <div className="kpi-card kpi-error">
            <div className="kpi-icon">
              <FaExclamationTriangle />
            </div>
            <div className="kpi-value">{kpis.exceptions}</div>
            <div className="kpi-label">Exceptions</div>
          </div>
        </section>

        {/* Main Layout - 2 columns */}
        <div className="main-layout">
          {/* Left Column - Transaction Table */}
          <div className="col-span-8">
            <div className="transaction-table">
              <div className="table-header">
                <h3 className="table-title">Employee Adjustments Register</h3>
                <div className="tab-buttons">
                  <button className={`tab-button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All</button>
                  <button className={`tab-button ${activeTab === 'incentive' ? 'active' : ''}`} onClick={() => setActiveTab('incentive')}>Incentives</button>
                  <button className={`tab-button ${activeTab === 'advance' ? 'active' : ''}`} onClick={() => setActiveTab('advance')}>Advances</button>
                  <button className={`tab-button ${activeTab === 'recovery' ? 'active' : ''}`} onClick={() => setActiveTab('recovery')}>Recoveries</button>
                  <button className={`tab-button ${activeTab === 'bonus' ? 'active' : ''}`} onClick={() => setActiveTab('bonus')}>Bonus</button>
                  <button className={`tab-button ${activeTab === 'deduction' ? 'active' : ''}`} onClick={() => setActiveTab('deduction')}>Penalties</button>
                </div>
                <div className="bulk-actions">
                  <label className="checkbox-label">
                    <input type="checkbox" className="checkbox-custom" onChange={handleSelectAll} checked={selectedRows.length === filteredTransactions.length && filteredTransactions.length > 0} />
                    <span>Select All</span>
                  </label>
                  <button className="btn-ghost-small"><FaCheckDouble /> Bulk Approve</button>
                  <button className="btn-ghost-small"><FaTimes /> Bulk Reject</button>
                  <button className="btn-ghost-small" onClick={handleMarkForPayroll}><FaFileInvoiceDollar /> Mark for Payroll</button>
                </div>
              </div>
              <div className="ag-grid-wrapper ag-theme-quartz-dark">
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredTransactions}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onSelectionChanged={onSelectionChanged}
                  rowHeight={44}
                  headerHeight={36}
                  domLayout="autoHeight"
                  suppressCellFocus={true}
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50, 100]}
                />
              </div>

              {/* ── Entry Type Distribution — below the table ── */}
              <div className="chart-card" style={{ marginTop: 16 }}>
                <div className="chart-card-title">Entry Type Distribution</div>
                <div className="donut-wrap">
                  <div className="donut-svg-wrap">
                    <svg viewBox="0 0 160 160" className="donut-svg">
                      <circle cx="80" cy="80" r="60" fill="none" stroke="var(--pay-surface-2)" strokeWidth="20" />
                      <circle cx="80" cy="80" r="60" fill="none" stroke="#4ADE80" strokeWidth="20"
                        strokeDasharray="165.9 211.1" strokeDashoffset="0" />
                      <circle cx="80" cy="80" r="60" fill="none" stroke="#F59E0B" strokeWidth="20"
                        strokeDasharray="82.9 294.1" strokeDashoffset="-165.9" />
                      <circle cx="80" cy="80" r="60" fill="none" stroke="#8660F8" strokeWidth="20"
                        strokeDasharray="49.0 328.0" strokeDashoffset="-248.8" />
                      <circle cx="80" cy="80" r="60" fill="none" stroke="#EF4444" strokeWidth="20"
                        strokeDasharray="30.2 346.8" strokeDashoffset="-297.8" />
                      <circle cx="80" cy="80" r="60" fill="none" stroke="#3B82F6" strokeWidth="20"
                        strokeDasharray="49.0 328.0" strokeDashoffset="-328.0" />
                    </svg>
                    <div className="donut-center-text">
                      <div className="donut-center-num">128</div>
                      <div className="donut-center-label">Total</div>
                    </div>
                  </div>
                  <div className="donut-legend">
                    {[
                      { label: 'Incentives', pct: '44%', color: '#4ADE80' },
                      { label: 'Advances',   pct: '22%', color: '#F59E0B' },
                      { label: 'Bonus',      pct: '13%', color: '#8660F8' },
                      { label: 'Deductions', pct: '8%',  color: '#EF4444' },
                      { label: 'Recoveries', pct: '13%', color: '#3B82F6' },
                    ].map(l => (
                      <div key={l.label} className="donut-legend-row">
                        <span className="chart-legend-dot" style={{ background: l.color }} />
                        <span className="donut-legend-label">{l.label}</span>
                        <span className="donut-legend-pct">{l.pct}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Add Panel */}
          <div className="col-span-4">
            <div className="space-y-5">
              {/* Quick Add Card */}
              <div className="quick-add-card">
                <h3 className="card-title"><FaPlusCircle /> Add Adjustment</h3>
                <div className="card-body">
                  <div>
                    <label className="form-label">Select Employee</label>
                    <select className="form-select" value={selectedEmployeeForEntry?.id || ''} onChange={(e) => {
                      const emp = employeesList.find(emp => emp.id === e.target.value);
                      setSelectedEmployeeForEntry(emp);
                    }}>
                      <option value="">Search employee...</option>
                      {employeesList.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.name} - {emp.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Entry Type</label>
                    <select className="form-select" value={selectedEntryType} onChange={(e) => setSelectedEntryType(e.target.value)}>
                      <option value="incentive">Incentive</option>
                      <option value="advance">Advance</option>
                      <option value="bonus">Bonus</option>
                      <option value="recovery">Recovery</option>
                      <option value="deduction">Deduction</option>
                    </select>
                  </div>

                  {/* Incentive Fields */}
                  {selectedEntryType === 'incentive' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Incentive Category</label>
                        <select className="form-input-sm" value={incentiveData.category} onChange={(e) => setIncentiveData({ ...incentiveData, category: e.target.value })}>
                          <option>Performance</option>
                          <option>Referral</option>
                          <option>Attendance</option>
                          <option>Festival</option>
                          <option>Sales</option>
                          <option>Custom</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label-sm">Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={incentiveData.amount} onChange={(e) => setIncentiveData({ ...incentiveData, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Notes</label>
                        <textarea rows="2" className="form-textarea-sm" placeholder="Add notes..." value={incentiveData.description} onChange={(e) => setIncentiveData({ ...incentiveData, description: e.target.value })}></textarea>
                      </div>
                    </div>
                  )}

                  {/* Advance Fields */}
                  {selectedEntryType === 'advance' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Advance Category</label>
                        <select className="form-input-sm" value={advanceData.category} onChange={(e) => setAdvanceData({ ...advanceData, category: e.target.value })}>
                          <option>Salary Advance</option>
                          <option>Emergency</option>
                          <option>Medical</option>
                          <option>Travel</option>
                          <option>Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label-sm">Advance Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={advanceData.amount} onChange={(e) => setAdvanceData({ ...advanceData, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Recovery Start Month</label>
                        <select className="form-input-sm" value={advanceData.recoveryStartMonth} onChange={(e) => setAdvanceData({ ...advanceData, recoveryStartMonth: e.target.value })}>
                          <option>May 2026</option>
                          <option>June 2026</option>
                          <option>July 2026</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label-sm">Number of Installments</label>
                        <input type="number" className="form-input-sm" placeholder="e.g. 6" value={advanceData.installments} onChange={(e) => setAdvanceData({ ...advanceData, installments: e.target.value })} />
                      </div>
                      <div className="toggle-item">
                        <span>Auto deduct every payroll</span>
                        <label className="toggle-switch">
                          <input type="checkbox" checked={advanceData.allowEarlyClosure} onChange={(e) => setAdvanceData({ ...advanceData, allowEarlyClosure: e.target.checked })} />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <div className="emi-preview">
                        <div className="emi-label">EMI Preview</div>
                        <div className="emi-value">₹{emiPreview.toFixed(0)} / month</div>
                      </div>
                    </div>
                  )}

                  {/* Deduction Fields */}
                  {selectedEntryType === 'deduction' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Reason</label>
                        <input type="text" className="form-input-sm" placeholder="Enter reason" value={deductionData.type} onChange={(e) => setDeductionData({ ...deductionData, type: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={deductionData.amount} onChange={(e) => setDeductionData({ ...deductionData, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Recovery Mode</label>
                        <select className="form-input-sm" value={deductionData.recoveryMode} onChange={(e) => setDeductionData({ ...deductionData, recoveryMode: e.target.value })}>
                          <option>Current Payroll</option>
                          <option>Installments</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="form-label">Effective Payroll Month</label>
                    <select className="form-select">
                      <option>April 2026</option>
                      <option>May 2026</option>
                      <option>June 2026</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Upload Proof</label>
                    <div className="upload-zone" onClick={() => document.getElementById('fileUpload').click()}>
                      <FaCloudUploadAlt className="upload-icon" />
                      <div className="upload-text">Click to upload or drag file</div>
                      <input type="file" id="fileUpload" style={{ display: 'none' }} onChange={(e) => setUploadedFile(e.target.files[0])} />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Remarks</label>
                    <textarea rows="2" className="form-textarea" placeholder="Optional remarks..." value={internalComments} onChange={(e) => setInternalComments(e.target.value)}></textarea>
                  </div>

                  <div className="action-row">
                    <button className="btn-secondary flex-1"><FaSave /> Save Draft</button>
                    <button className="btn-primary flex-1" onClick={handleSubmitEntry}><FaPaperPlane /> Submit</button>
                  </div>
                </div>
              </div>

              {/* Employee Snapshot */}
              <div className="snapshot-card">
                <h4 className="snapshot-title"><FaUserCircle /> Employee Snapshot</h4>
                <div className="snapshot-employee">
                  <img src={selectedEmployeeDetails?.avatar} alt={selectedEmployeeDetails?.name} className="snapshot-avatar" />
                  <div>
                    <div className="snapshot-name">{selectedEmployeeDetails?.name || 'Select Employee'}</div>
                    <div className="snapshot-dept">{selectedEmployeeDetails?.department || '—'}</div>
                  </div>
                </div>
                <div className="snapshot-details">
                  <div className="detail-row"><span>Current Gross Salary</span><span>₹{selectedEmployeeDetails?.grossSalary?.toLocaleString() || '—'}</span></div>
                  <div className="detail-row"><span>Last 3 Months Incentives</span><span className="success-text">₹8,500</span></div>
                  <div className="detail-row"><span>Outstanding Advance</span><span className="warning-text">₹0</span></div>
                  <div className="detail-row"><span>This Month Net Impact</span><span className={netImpact.newAmount >= 0 ? 'success-text' : 'error-text'}>{netImpact.newAmount >= 0 ? '+' : ''}₹{Math.abs(netImpact.newAmount).toLocaleString()}</span></div>
                  <div className="detail-row border-top"><span>Payroll Status</span><span className="status-chip status-approved">Ready</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* -- ADD NEW ADJUSTMENT � Full Modal -- */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="full-modal" onClick={(e) => e.stopPropagation()}>
            <div className="full-modal-header">
              <div>
                <h2 className="full-modal-title">Add New Adjustment</h2>
                <p className="full-modal-sub">Create payroll adjustment for an employee</p>
              </div>
              <div className="full-modal-header-actions">
                <button className="btn-secondary"><FaSave /> Save Draft</button>
                <button className="btn-primary" onClick={handleSubmitEntry}><FaPaperPlane /> Submit</button>
                <button className="full-modal-close" onClick={() => setShowAddModal(false)}>�</button>
              </div>
            </div>
            <div className="full-modal-body">
              <div className="full-modal-left">
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">1</span> Employee Selection</h4>
                  <div className="employee-search-wrapper">
                    <FaSearch className="search-icon-inline" />
                    <input type="text" className="employee-search-input" placeholder="Search by name or ID..." value={employeeSearchTerm} onChange={(e) => setEmployeeSearchTerm(e.target.value)} />
                  </div>
                  {filteredEmployees.length > 0 && (
                    <div className="employee-list">
                      {filteredEmployees.map(emp => (
                        <div key={emp.id} className={`employee-item ${selectedEmployeeForEntry?.id === emp.id ? 'selected' : ''}`} onClick={() => setSelectedEmployeeForEntry(emp)}>
                          <img src={emp.avatar} alt={emp.name} className="employee-avatar-md" />
                          <div className="employee-info">
                            <div className="font-semibold">{emp.name}</div>
                            <div className="employee-meta">{emp.id} � {emp.department} � {emp.designation}</div>
                          </div>
                          {selectedEmployeeForEntry?.id === emp.id && <FaCheck className="check-icon" />}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedEmployeeForEntry && (
                    <div className="selected-employee-strip">
                      <img src={selectedEmployeeForEntry.avatar} alt={selectedEmployeeForEntry.name} className="selected-avatar" />
                      <div className="selected-details">
                        <div><span>Name</span><div>{selectedEmployeeForEntry.name}</div></div>
                        <div><span>Emp ID</span><div>{selectedEmployeeForEntry.id}</div></div>
                        <div><span>Department</span><div>{selectedEmployeeForEntry.department}</div></div>
                        <div><span>Designation</span><div>{selectedEmployeeForEntry.designation}</div></div>
                        <div><span>Branch</span><div>{selectedEmployeeForEntry.branch}</div></div>
                        <div><span>Monthly Gross</span><div className="success-text">?{selectedEmployeeForEntry.grossSalary.toLocaleString()}</div></div>
                      </div>
                      <span className="status-chip status-approved">Ready</span>
                    </div>
                  )}
                </div>
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">2</span> Select Adjustment Type</h4>
                  <div className="type-buttons">
                    {[{ key: 'incentive', icon: <FaGift />, label: 'Incentive' }, { key: 'advance', icon: <FaHandHoldingUsd />, label: 'Advance' }, { key: 'bonus', icon: <FaTrophy />, label: 'Bonus' }, { key: 'recovery', icon: <FaUndoAlt />, label: 'Recovery' }, { key: 'deduction', icon: <FaMinusCircle />, label: 'Deduction' }].map(t => (
                      <button key={t.key} className={`type-btn ${selectedEntryType === t.key ? 'active' : ''}`} onClick={() => setSelectedEntryType(t.key)}>{t.icon}{t.label}</button>
                    ))}
                  </div>
                </div>
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">3</span> Adjustment Details</h4>
                  {selectedEntryType === 'incentive' && (
                    <>
                      <div className="form-grid">
                        <div><label>Incentive Category</label><select value={incentiveData.category} onChange={(e) => setIncentiveData({ ...incentiveData, category: e.target.value })}>{AI.incentiveCategories.map(o => <option key={o}>{o}</option>)}</select></div>
                        <div><label>Amount (?)</label><input type="number" placeholder="Enter amount" value={incentiveData.amount} onChange={(e) => setIncentiveData({ ...incentiveData, amount: e.target.value })} /></div>
                        <div><label>Effective Month</label><select value={incentiveData.effectiveMonth} onChange={(e) => setIncentiveData({ ...incentiveData, effectiveMonth: e.target.value })}>{['April 2026', 'May 2026', 'June 2026'].map(o => <option key={o}>{o}</option>)}</select></div>
                        <div><label>Recurring</label><select value={incentiveData.recurring} onChange={(e) => setIncentiveData({ ...incentiveData, recurring: e.target.value })}>{['One Time', 'Monthly', 'Quarterly'].map(o => <option key={o}>{o}</option>)}</select></div>
                      </div>
                      <div className="toggle-item mt-3"><span>Taxable</span><label className="toggle-switch"><input type="checkbox" checked={incentiveData.taxable} onChange={(e) => setIncentiveData({ ...incentiveData, taxable: e.target.checked })} /><span className="toggle-slider" /></label></div>
                      <div className="mt-3"><label>Description / Reason</label><textarea rows="3" placeholder="Provide reason..." value={incentiveData.description} onChange={(e) => setIncentiveData({ ...incentiveData, description: e.target.value })} /></div>
                      <div className="mt-3"><label>Upload Proof</label><div className="upload-zone"><FaCloudUploadAlt className="upload-icon" /><div className="upload-text">Click to upload or drag and drop</div><div style={{ fontSize: 10, color: 'var(--pay-text-muted)', marginTop: 4 }}>PDF, DOC, JPG up to 10MB</div></div></div>
                    </>
                  )}
                  {selectedEntryType === 'advance' && (
                    <>
                      <div className="form-grid">
                        <div><label>Advance Category</label><select value={advanceData.category} onChange={(e) => setAdvanceData({ ...advanceData, category: e.target.value })}>{AI.advanceCategories.map(o => <option key={o}>{o}</option>)}</select></div>
                        <div><label>Advance Amount (?)</label><input type="number" placeholder="Enter amount" value={advanceData.amount} onChange={(e) => setAdvanceData({ ...advanceData, amount: e.target.value })} /></div>
                        <div><label>Disbursement Date</label><input type="date" value={advanceData.disbursementDate} onChange={(e) => setAdvanceData({ ...advanceData, disbursementDate: e.target.value })} /></div>
                        <div><label>Recovery Start Month</label><select value={advanceData.recoveryStartMonth} onChange={(e) => setAdvanceData({ ...advanceData, recoveryStartMonth: e.target.value })}>{['May 2026', 'June 2026', 'July 2026'].map(o => <option key={o}>{o}</option>)}</select></div>
                      </div>
                      <div className="mt-3"><label>Recovery Method</label><div className="modal-radio-group"><label className="modal-radio-label"><input type="radio" name="rm" value="Full Next Salary" checked={advanceData.recoveryMethod === 'Full Next Salary'} onChange={(e) => setAdvanceData({ ...advanceData, recoveryMethod: e.target.value })} />Full Next Salary</label><label className="modal-radio-label"><input type="radio" name="rm" value="EMI Installments" checked={advanceData.recoveryMethod === 'EMI Installments'} onChange={(e) => setAdvanceData({ ...advanceData, recoveryMethod: e.target.value })} />EMI Installments</label></div></div>
                      {advanceData.recoveryMethod === 'EMI Installments' && <div className="form-grid mt-3"><div><label>Installments</label><input type="number" placeholder="e.g. 6" value={advanceData.installments} onChange={(e) => setAdvanceData({ ...advanceData, installments: e.target.value })} /></div><div><label>EMI Amount (Auto)</label><input type="text" readOnly value={emiPreview > 0 ? `?${emiPreview.toFixed(0)} / month` : ''} placeholder="Auto calculated" /></div></div>}
                      <div className="form-grid mt-3"><div className="toggle-item"><span>Allow Early Closure</span><label className="toggle-switch"><input type="checkbox" checked={advanceData.allowEarlyClosure} onChange={(e) => setAdvanceData({ ...advanceData, allowEarlyClosure: e.target.checked })} /><span className="toggle-slider" /></label></div><div className="toggle-item"><span>Approval Required</span><label className="toggle-switch"><input type="checkbox" checked={advanceData.approvalRequired} onChange={(e) => setAdvanceData({ ...advanceData, approvalRequired: e.target.checked })} /><span className="toggle-slider" /></label></div></div>
                      <div className="mt-3"><label>Notes</label><textarea rows="2" placeholder="Add notes..." value={advanceData.notes} onChange={(e) => setAdvanceData({ ...advanceData, notes: e.target.value })} /></div>
                      <div className="mt-3"><label>Upload Request Proof</label><div className="upload-zone"><FaCloudUploadAlt className="upload-icon" /><div className="upload-text">Click to upload or drag and drop</div><div style={{ fontSize: 10, color: 'var(--pay-text-muted)', marginTop: 4 }}>PDF, DOC, JPG up to 10MB</div></div></div>
                    </>
                  )}
                  {selectedEntryType === 'bonus' && (
                    <>
                      <div className="form-grid">
                        <div><label>Bonus Type</label><select value={bonusData.type} onChange={(e) => setBonusData({ ...bonusData, type: e.target.value })}>{AI.bonusTypes.map(o => <option key={o}>{o}</option>)}</select></div>
                        <div><label>Amount (?)</label><input type="number" placeholder="Enter amount" value={bonusData.amount} onChange={(e) => setBonusData({ ...bonusData, amount: e.target.value })} /></div>
                      </div>
                      <div className="mt-3"><label>Effective Month</label><select value={bonusData.effectiveMonth} onChange={(e) => setBonusData({ ...bonusData, effectiveMonth: e.target.value })}>{['April 2026', 'May 2026', 'June 2026'].map(o => <option key={o}>{o}</option>)}</select></div>
                      <div className="mt-3"><label>Notes</label><textarea rows="2" placeholder="Add notes..." value={bonusData.notes} onChange={(e) => setBonusData({ ...bonusData, notes: e.target.value })} /></div>
                    </>
                  )}
                  {selectedEntryType === 'deduction' && (
                    <>
                      <div className="form-grid">
                        <div><label>Deduction Type</label><select value={deductionData.type} onChange={(e) => setDeductionData({ ...deductionData, type: e.target.value })}>{AI.deductionTypes.map(o => <option key={o}>{o}</option>)}</select></div>
                        <div><label>Amount (?)</label><input type="number" placeholder="Enter amount" value={deductionData.amount} onChange={(e) => setDeductionData({ ...deductionData, amount: e.target.value })} /></div>
                      </div>
                      <div className="mt-3"><label>Recovery Mode</label><select value={deductionData.recoveryMode} onChange={(e) => setDeductionData({ ...deductionData, recoveryMode: e.target.value })}>{['Current Payroll (Full)', 'Installments'].map(o => <option key={o}>{o}</option>)}</select></div>
                      <div className="mt-3"><label>Remarks</label><textarea rows="2" placeholder="Add remarks..." value={deductionData.remarks} onChange={(e) => setDeductionData({ ...deductionData, remarks: e.target.value })} /></div>
                    </>
                  )}
                  {selectedEntryType === 'recovery' && (
                    <>
                      <div className="mt-3"><label>Select Existing Advance Case</label><select value={recoveryData.advanceCase} onChange={(e) => setRecoveryData({ ...recoveryData, advanceCase: e.target.value })}><option value="ADV-2024-001">ADV-2024-001 � Emergency Advance ?15,000</option><option value="ADV-2024-002">ADV-2024-002 � Medical Advance ?8,000</option></select></div>
                      <div className="recovery-summary-row mt-3">{[['Total Advance', '?15,000'], ['Recovered So Far', '?6,000'], ['Outstanding', '?9,000']].map(([l, v]) => <div key={l} className="recovery-summary-cell"><div className="recovery-summary-label">{l}</div><div className="recovery-summary-val">{v}</div></div>)}</div>
                      <div className="form-grid mt-3">
                        <div><label>Recovery This Month (?)</label><input type="number" placeholder="Enter amount" value={recoveryData.recoveryAmount} onChange={(e) => setRecoveryData({ ...recoveryData, recoveryAmount: e.target.value })} /></div>
                        <div><label>Remaining Balance (?)</label><input type="text" readOnly placeholder="Auto calculated" value={recoveryData.recoveryAmount ? `?${(9000 - parseFloat(recoveryData.recoveryAmount || 0)).toLocaleString()}` : ''} /></div>
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-section">
                  <h4 className="section-title">Internal Comments</h4>
                  <textarea rows="3" placeholder="Add internal notes for HR/Payroll team..." value={internalComments} onChange={(e) => setInternalComments(e.target.value)} />
                </div>
                <div className="full-modal-footer">
                  <button className="btn-ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button className="btn-secondary"><FaSave /> Save Draft</button>
                  <button className="btn-primary" onClick={handleSubmitEntry}><FaPaperPlane /> Submit for Approval</button>
                </div>
              </div>
              <div className="full-modal-right">
                <div className="modal-sidebar-card">
                  <div className="modal-sidebar-title"><FaUserCircle /> Employee Payroll Snapshot</div>
                  {selectedEmployeeForEntry ? (
                    <>
                      <div className="modal-emp-info">
                        <img src={selectedEmployeeForEntry.avatar} alt={selectedEmployeeForEntry.name} className="snapshot-avatar" />
                        <div><div className="snapshot-name">{selectedEmployeeForEntry.name}</div><div className="snapshot-dept">{selectedEmployeeForEntry.department}</div></div>
                      </div>
                      <div className="snapshot-details">
                        {[['Current Gross', `?${selectedEmployeeForEntry.grossSalary.toLocaleString()}`, ''], [`Current Net`, '?38,500', ''], ['Incentives This Month', '+?3,000', 'success-text'], ['Outstanding Advance', '?9,000', 'warning-text'], ['Deductions', '-?2,500', 'error-text'], ['Payroll Lock', 'Open', 'success-text']].map(([l, v, c]) => (
                          <div key={l} className="detail-row"><span>{l}</span><span className={c}>{v}</span></div>
                        ))}
                      </div>
                    </>
                  ) : <div className="modal-empty-state">Select an employee to see snapshot</div>}
                </div>
                <div className="modal-sidebar-card">
                  <div className="modal-sidebar-title"><FaExclamationTriangle /> Policy Checks</div>
                  <div className="policy-list">
                    <div className="policy-item policy-warning"><FaExclamationTriangle className="policy-icon warning-text" /><div><div className="policy-title">Manager Approval Required</div><div className="policy-desc">Incentive exceeds auto-approval limit of ?2,500</div></div></div>
                    <div className="policy-item policy-warning"><FaExclamationTriangle className="policy-icon warning-text" /><div><div className="policy-title">Active Advance Detected</div><div className="policy-desc">Employee has ?9,000 outstanding advance balance</div></div></div>
                    <div className="policy-item policy-success"><FaCheckCircle className="policy-icon success-text" /><div><div className="policy-title">Eligible for Festival Bonus</div><div className="policy-desc">Employee completed 1 year tenure</div></div></div>
                  </div>
                </div>
                {selectedEmployeeForEntry && (
                  <div className="modal-sidebar-card">
                    <div className="modal-sidebar-title"><FaChartLine /> Net Impact Preview</div>
                    <div className="net-impact-preview" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                      <div className="impact-row"><span>Current Net Salary</span><span>?{netImpact.currentNet.toLocaleString()}</span></div>
                      <div className="impact-delta"><span className={netImpact.newAmount >= 0 ? 'success-text' : 'error-text'}>{netImpact.newAmount >= 0 ? '+' : ''}?{Math.abs(netImpact.newAmount).toLocaleString()}</span></div>
                      <div className="impact-total"><span>Projected Net Salary</span><span className="accent-text">?{netImpact.projectedNet.toLocaleString()}</span></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Bottom Status Bar */}
      <div className="bottom-status-bar">
        <div className="status-left">
          <div className="status-label">Payroll Readiness</div>
          <div className="status-stats">
            <span className="stat"><FaFileInvoiceDollar /> <strong>{transactions.length}</strong> Entries</span>
            <span className="stat"><FaCheckCircle /> <strong>{transactions.filter(t => t.status === 'approved').length}</strong> Approved</span>
            <span className="stat"><FaClock /> <strong>{transactions.filter(t => t.status === 'pending').length}</strong> Pending</span>
            <span className="stat"><FaExclamationCircle /> <strong>{transactions.filter(t => t.status === 'rejected').length}</strong> Errors</span>
          </div>
        </div>
        <div className="progress-container">
          <div className="progress-label">
            <span>Completion</span>
            <span className="success-text">{Math.round((transactions.filter(t => t.status === 'approved' || t.status === 'processed').length / transactions.length) * 100)}%</span>
          </div>
          <div className="progress-bar-bg">
            <div className="progress-bar-fill" style={{ width: `${Math.round((transactions.filter(t => t.status === 'approved' || t.status === 'processed').length / transactions.length) * 100)}%` }}></div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default IncentivesAdvances;
